import { useState, useMemo } from "react";

// Material UI
import { Icon, Grid } from "@mui/material";
// form components
import { SelectField } from "src/components/FormikComponents/SelectField";

// Utility functions
import { isIsoDate } from "src/utils/index";

// DataType Filters
import { EventFilter } from "../../SearchFilters/EventFilter";
import { PairingFilter } from "../../SearchFilters/PairingFilter";
import { ContentEventFilter } from "../../SearchFilters/ContentEventFilter";
import { CharterOverviewFilter } from "../../SearchFilters/CharterOverviewFilter";
import { UnbookedFilter } from "../../SearchFilters/UnbookedFilter";
import { BookingFilter } from "../../SearchFilters/BookingFilter";
import { ISearchStatus } from "../../SearchFilters/Helpers";
import { SavedSearchFilters } from "./SavedSearchFilters";
import { ISavedFilter, IFilterOption } from "@vmosolutions/oc-interfaces";

interface ITableControls {
  events?: any[];
  selectedEvent?: any;
  onSelectEvent?: (event: any) => void;
  hideFilter?: boolean;
  hideShowColumns?: boolean;
  selectedFilters?: any[];
  onSelectFilters?: (filter: any[]) => void;
  onFilterValues?: (values: any) => void;
  dataset?: any;
  onFilteredEvents?: (data: any) => any;
  columns?: any[];
  selectedColumns?: any[];
  onSelectColumns?: (columns: any[]) => void;
  parentElement?: any;
  page?: number;
  changePage?: (number: number) => void;
  allowMultiFilter?: boolean;
  searchStatus?: (status: ISearchStatus) => void;
  showDateRangeRRule?: boolean;
  optionalControls?: JSX.Element;
  showSavedFilters?: boolean;
}

/////////////////////////////////////////////////////
///////////    FILTER TABLE
/////////////////////////////////////////////////////
/**
 * Function checks what option the user selected from the drop down menu and accepts the value provided by the
 * user, from either the DateTimeInput or TextFieldInput, and changes the table to display flights with
 * that given value
 * @param content -
 * @param selectedFilters -
 * @param filters -
 */
export const FilterTable = (
  content: any[],
  selectedFilters: string[],
  filters: any,
  changeFilteredRows?: any
): any[] => {
  const charRegex = /[^\w\s]/g;
  let newList: any[];
  if (selectedFilters.length >= 1) {
    const filterKeys = Object.keys(filters);
    newList = content.filter((contentRow: any) => {
      return filterKeys.every((eachFilter: any) => {
        const inputValue = filters[eachFilter];
        if (!inputValue.length) {
          // Checks if it is a date and time input
          if (typeof inputValue === "object") {
            const actualDate = contentRow[eachFilter];
            const userInputMin = inputValue.minTime;
            const userInputMax = inputValue.maxTime;
            const userMinDateInput = new Date(userInputMin);
            const userMaxDateInput = new Date(userInputMax);
            const newActualDate = new Date(contentRow[eachFilter]);
            if (isIsoDate(actualDate).type === "isoString") {
              if (userInputMax !== "" && userInputMin !== "") {
                return newActualDate >= userMinDateInput && newActualDate <= userMaxDateInput;
              }
            } else if (Object.prototype.toString.call(actualDate) === "[object Date]") {
              if (userInputMax !== "" && userInputMin !== "") {
                return newActualDate >= userMinDateInput && newActualDate <= userMaxDateInput;
              }
              return true;
            }
            return true;
          }
          return true;
        } else {
          if (eachFilter.match(charRegex) === null) {
            if (typeof contentRow[eachFilter] === "number") {
              return contentRow[eachFilter].toString().startsWith(inputValue);
            }
            if (typeof contentRow[eachFilter] === "string") {
              return contentRow[eachFilter].toUpperCase().includes(inputValue);
            }
            return true;
          } else {
            const indexWithChar: number = eachFilter.search(charRegex);
            const firstDataName: string = eachFilter.substring(0, indexWithChar);
            const secondDataName: string = eachFilter.substring(indexWithChar + 1, eachFilter.length - 1);
            const eachItem = contentRow[firstDataName];
            if (!eachItem) {
              return false;
            } //it had the worng content... when it should be ground event data it has flight data
            // Checks if the current item is an object within an array or just an object
            // Assumption: that the array will always be size one and so I would only need to get the first item in the array
            if (eachItem.length > 0) {
              return contentRow[firstDataName]["0"][secondDataName].toUpperCase().includes(inputValue);
            }
            // If the length is less than 0, then this means that it's just an object or that the type is undefined
            else {
              if (typeof contentRow[firstDataName][secondDataName] === "undefined") {
                if (inputValue === "NONE") {
                  return contentRow[firstDataName];
                }
                return false;
              } else {
                return contentRow[firstDataName][secondDataName].toUpperCase().includes(inputValue);
              }
            }
          }
        }
      });
    });
  } else {
    newList = content;
  }
  if (changeFilteredRows) changeFilteredRows(newList); // Callback function
  return newList;
};

/**
 * Function that maps out the headings of the columns in the table and also allows users to display the data in a desc or asc order
 * @param props -
 */
export const TableControls = (props: ITableControls): JSX.Element => {
  const {
    events,
    selectedEvent,
    onSelectEvent,
    hideFilter,
    hideShowColumns,
    onFilteredEvents,
    //dataset,
    columns,
    selectedColumns,
    onSelectColumns,
    parentElement,
    allowMultiFilter,
    searchStatus,
    showDateRangeRRule,
    optionalControls,
    showSavedFilters,
  } = props; //page, changePage

  const { dataType } = selectedEvent;
  const [applySavedFilter, setAppliedSavedFilter] = useState<ISavedFilter>(null);
  // Used to pass the search Filter from event/pairing filters over to the SavedSearchFilters component.
  const [searchFilters, setSearchFilters] = useState<IFilterOption[]>([]);

  // Saved searches will be displayed only for the following dataTypes at the moment.
  const displaySavedSearches: boolean = useMemo(() => {
    return showSavedFilters &&
      (dataType === "content" || dataType === "flights" || dataType === "groundEvents" || dataType === "pairings")
      ? true
      : false;
  }, [dataType, showSavedFilters]);

  /**
   * Update parent search table with filtered results.
   * @param events -
   */
  const handleFilteredEvents = (filteredEvents: any): void => {
    // onFilteredEvents((hideFilter) ? events : filteredEvents)
    onFilteredEvents(filteredEvents);
  };

  return (
    <div className="table_controls">
      <Grid container direction="column">
        {displaySavedSearches && (
          <SavedSearchFilters
            dataType={dataType}
            searchFilters={searchFilters} // Search filters passed up from Filter components
            parentElement={parentElement}
            setAppliedSavedFilter={setAppliedSavedFilter}
          />
        )}
        {!!events && events.length > 1 && (
          <Grid container wrap="nowrap" style={{ width: "auto" }} className="table_eventtype_control">
            <Icon className="control_icon">event</Icon>
            <Grid item>
              <SelectField
                className="table_event_selection"
                label=""
                name="selectedEvent"
                value={selectedEvent}
                onChange={(value: any) => onSelectEvent(value)}
                options={events}
                // getOptionLabel={(AC: IACOption) => `${AC.registration} (${AC.title})`}
                getOptionValue={(item: any) => item.id}
                // noOptionsMessage={() => "Search for airport name or IATA code"}
                isSearchable={true}
                isClearable={false}
                menuPosition="fixed"
                maxMenuHeight={400}
                openMenuOnFocus={false}
                menuPortalTarget={parentElement}
                placeholder="Select event type to view..."
              />
            </Grid>
          </Grid>
        )}

        {!hideFilter && (
          <div className="table_filter_control">
            {dataType === "flights" || dataType === "groundEvents" ? (
              <EventFilter
                dataType={dataType}
                // dataInput={dataset}
                dataOutput={handleFilteredEvents}
                parentElement={parentElement}
                allowMultiFilter={allowMultiFilter}
                hideLabel={true}
                searchStatus={searchStatus}
                showDateRangeRRule={showDateRangeRRule}
                setSearchFilters={setSearchFilters} // used to pass filters up to this components so it can be used in the SavedSearchFilters component.
                applySavedFilter={applySavedFilter}
              />
            ) : dataType === "pairings" ? (
              <PairingFilter
                dataType={dataType}
                // dataInput={dataset}
                dataOutput={handleFilteredEvents}
                parentElement={parentElement}
                allowMultiFilter={allowMultiFilter}
                hideLabel={true}
                searchStatus={searchStatus}
                showDateRangeRRule={showDateRangeRRule}
                setSearchFilters={setSearchFilters} // used to pass filters up to this components so it can be used in the SavedSearchFilters component.
                applySavedFilter={applySavedFilter}
              />
            ) : dataType === "content" ? (
              <ContentEventFilter
                dataType={dataType}
                // dataInput={dataset}
                dataOutput={handleFilteredEvents}
                parentElement={parentElement}
                allowMultiFilter={allowMultiFilter}
                hideLabel={true}
                searchStatus={searchStatus}
                showDateRangeRRule={showDateRangeRRule}
                setSearchFilters={setSearchFilters} // used to pass filters up to this components so it can be used in the SavedSearchFilters component.
                applySavedFilter={applySavedFilter}
              />
            ) : dataType === "charterPassengerOverview" ? (
              <CharterOverviewFilter
                dataType={dataType}
                dataInput={events[0].data}
                dataOutput={handleFilteredEvents}
                parentElement={parentElement}
                allowMultiFilter={allowMultiFilter}
                hideLabel={true}
                searchStatus={searchStatus}
                showDateRangeRRule={showDateRangeRRule}
              />
            ) : dataType === "unbookedFlights" ? (
              <UnbookedFilter
                dataType={dataType}
                dataInput={events[0].data}
                dataOutput={handleFilteredEvents}
                parentElement={parentElement}
                allowMultiFilter={allowMultiFilter}
                hideLabel={true}
                searchStatus={searchStatus}
                showDateRangeRRule={showDateRangeRRule}
              />
            ) : dataType === "bookings" ? (
              <BookingFilter
                dataType={dataType}
                dataInput={events[0].data}
                dataOutput={handleFilteredEvents}
                parentElement={parentElement}
                allowMultiFilter={allowMultiFilter}
                hideLabel={true}
                searchStatus={searchStatus}
                showDateRangeRRule={showDateRangeRRule}
              />
            ) : null}
          </div>
        )}

        {!!columns && !hideShowColumns && (
          <Grid container alignItems="center">
            <Grid container wrap="nowrap" style={{ width: "auto" }} className="table_columnview_control">
              <Icon className="control_icon">view_column</Icon>
              <Grid item>
                <SelectField
                  className="show_hide_selection"
                  label=""
                  name="selectedColumns"
                  value={selectedColumns}
                  onChange={(value: any) => onSelectColumns(value)}
                  options={columns}
                  // getOptionLabel={(AC: IACOption) => `${AC.registration} (${AC.title})`}
                  getOptionValue={(item: any) => item.id}
                  // noOptionsMessage={() => "Search for airport name or IATA code"}
                  isSearchable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  openMenuOnFocus={false}
                  menuPortalTarget={parentElement}
                  placeholder="Show/Hide columns..."
                  isMulti={true}
                  isClearable={false}
                  multiMode="checkbox"
                  hideSelectedValues={true}
                />
              </Grid>
            </Grid>
            {optionalControls}
          </Grid>
        )}
      </Grid>
    </div>
  );
};
